import { GenericStore } from 'src/util/mobx/generic-store/generic.store'
import { createStore } from 'src/util/mobx/generic-store/store-creator'
import { GenericStoreAsyncMethod } from 'src/util/mobx/generic-store/decorator/async-method.decorator'
import * as ProjectBuilderApi from '../api/project-builder-api'
import { ProjectRequirements, MappedSkillsResponse, ProjectSkill, CreateProjectDto, CreateProjectResponse, AnalyzeProjectRequirementsDto, ProjectListItemDto, ProjectDetailsDto, RecommendedUsersDto } from '../../project-builder/model/project-builder'

export class ProjectBuilderStore extends GenericStore {
  projectRequirements?: ProjectRequirements
  mappedSkills?: MappedSkillsResponse
  createProjectResponse?: CreateProjectResponse
  projectName = ''
  projectDescription = ''
  startDate = ''
  endDate = ''
  projectsOverview: ProjectListItemDto[] = []
  projectDetails?: ProjectDetailsDto
  recommendedUsers?: RecommendedUsersDto

  constructor() {
    super('projectBuilderStore')

    super.observe(this)
  }

  reset() {
    this.projectRequirements = undefined
    this.mappedSkills = undefined
    this.createProjectResponse = undefined
    this.projectName = ''
    this.projectDescription = ''
    this.startDate = ''
    this.endDate = ''
    this.projectsOverview = []
    this.projectDetails = undefined
    this.recommendedUsers = undefined
  }

  @GenericStoreAsyncMethod()
  async analyzeProjectRequirements(
    projectName: string,
    projectDescription: string,
    startDate: string,
    endDate: string,
  ) {
    // Save this in store so modal can autofill
    this.projectName = projectName
    this.projectDescription = projectDescription
    this.startDate = startDate
    this.endDate = endDate

    const dto: AnalyzeProjectRequirementsDto = {
      projectName,
      projectDescription,
      startDate,
      endDate,
    }

    this.projectRequirements = await ProjectBuilderApi.analyzeProjectRequirements(dto)
  }
  
  @GenericStoreAsyncMethod()
  async mapSkillsToOrganisation(suggestedSkills: ProjectSkill[]) {
    this.mappedSkills = await ProjectBuilderApi.mapSkillsToOrganisation(suggestedSkills)
  }

  @GenericStoreAsyncMethod()
  async createProject(
    name: string,
    startDate: string,
    endDate: string,
    selectedUserIds: string[],
    notifyUsers: boolean,

  ) {
    if (!this.projectRequirements?.skills?.length) {
      return
    }
    
    const dto: CreateProjectDto = {
      name,
      description: this.projectDescription,
      startDate,
      endDate,
      selectedUserIds,
      skills: this.projectRequirements.skills,
      notifyUsers,
    }

    this.createProjectResponse = await ProjectBuilderApi.createProject(dto)
    
    return this.createProjectResponse.projectId
  }

  @GenericStoreAsyncMethod()
  async getProjects() {
    this.projectsOverview = await ProjectBuilderApi.getProjects()
  }

  @GenericStoreAsyncMethod()
  async getProjectDetails(projectId: string) {
    this.projectDetails = await ProjectBuilderApi.getProjectDetails(projectId)
  }

  @GenericStoreAsyncMethod()
  async getRecommendedUsers(projectId: string) {
    this.recommendedUsers = await ProjectBuilderApi.getRecommendedUsers(projectId)
  }

  @GenericStoreAsyncMethod()
  async updateProjectDates(
    projectId: string,
    startDate: string,
    endDate: string,
  ) {
    this.projectDetails = await ProjectBuilderApi.updateProjectDates(projectId, {
      startDate,
      endDate,
    })
  }

  @GenericStoreAsyncMethod()
  async removeProjectUser(projectId: string, userId: string) {
    this.projectDetails = await ProjectBuilderApi.removeProjectUser(projectId, userId)
  }

  @GenericStoreAsyncMethod()
  async addUsersToProject(projectId: string, userIds: string[], notifyUsers: boolean) {
    await ProjectBuilderApi.addUsersToProject(projectId, userIds, notifyUsers)

    this.projectDetails = await ProjectBuilderApi.getProjectDetails(projectId)
    this.recommendedUsers = await ProjectBuilderApi.getRecommendedUsers(projectId)
  }

  @GenericStoreAsyncMethod()
  async respondToProjectInvitation(
    projectId: string,
    accepted: boolean,
  ) {
    return await ProjectBuilderApi.respondToProjectInvitation(
      projectId,
      accepted,
    )
  }

  @GenericStoreAsyncMethod()
  async deleteProject(projectId: string) {
    await ProjectBuilderApi.deleteProject(projectId)

    this.projectsOverview = this.projectsOverview.filter(project => project.id !== projectId)
  }
}

export const {
  storeInstance: projectBuilderStoreInstance,
  useStore: useProjectBuilderStore,
} = createStore(new ProjectBuilderStore())